import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  BookOpen,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Sparkles,
  FileText,
  ShieldCheck,
  Quote,
  BadgeCheck,
  Ban,
  Loader2,
  Pencil,
  Save,
} from "lucide-react";
import AppShell from "../AppShell";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { competencyCategories } from "../data/competencies";
import {
  PageHeader,
  ErrorBanner,
  EmptyState,
  LoadingRow,
} from "../../components/primitives";

const competencyNameById = Object.fromEntries(
  competencyCategories.flatMap((cat) => cat.modules.map((m) => [m.id, m.name]))
);
const displayCompetency = (m) =>
  competencyNameById[m.competency_id] ||
  (m.competency_name && m.competency_name !== m.competency_id
    ? m.competency_name
    : m.competency_id);

/* Status pill */
// Canonical status grouping for this page — mirrors MODULE_LIVE_STATES /
// MODULE_BLOCKED_STATES in backend/server.py. `learning_modules` is written
// by two pipelines with different vocabularies (draft/approved/rejected vs
// processing/ready/active/archived/failed); without this, a module cleared
// via the new pipeline (or approved-then-assigned, which flips it to
// "active") silently fell through to the "Draft" default below and could
// never be found under the Approved tab.
function statusGroup(status) {
  if (status === "approved" || status === "ready" || status === "active") return "approved";
  if (status === "rejected" || status === "failed" || status === "archived") return "rejected";
  return "draft"; // draft or processing — not yet cleared for staff
}

function StatusPill({ status }) {
  const map = {
    draft: { label: "Draft", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  };
  const s = map[statusGroup(status)];
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={`text-[10.5px] uppercase tracking-[0.16em] font-semibold border rounded px-2 py-0.5 ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

/* ---------------- LIST VIEW ---------------- */

function LearningList() {
  const { business } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    try {
      const data = await api.get("/learning-modules");
      setModules(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return modules;
    return modules.filter((m) => statusGroup(m.status) === statusFilter);
  }, [modules, statusFilter]);

  const counts = useMemo(
    () => ({
      all: modules.length,
      draft: modules.filter((m) => statusGroup(m.status) === "draft").length,
      approved: modules.filter((m) => statusGroup(m.status) === "approved").length,
      rejected: modules.filter((m) => statusGroup(m.status) === "rejected").length,
    }),
    [modules]
  );

  const grouped = useMemo(() => {
    const map = new Map();
    for (const m of filtered) {
      const key = m.competency_id || "uncategorised";
      if (!map.has(key)) map.set(key, { competency_name: displayCompetency(m), modules: [] });
      map.get(key).modules.push(m);
    }
    return Array.from(map.entries()).map(([id, v]) => ({ id, ...v }));
  }, [filtered]);

  return (
    <AppShell>
      <PageHeader
        testid="learning-page"
        kicker="AI Learning workspace"
        title="Generated learning & assessments"
        description="AI drafts a learning module and question bank from every SOP linked to a competency. Review each section and every question against the source document before approving for staff assessment."
        action={
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shrink-0 hidden md:block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              Drafts awaiting review
            </div>
            <div className="mt-1 text-2xl font-display text-slate-900 font-semibold">
              {counts.draft}
            </div>
          </div>
        }
      />

      {business?.is_demo && (
        <div className="mb-5 rounded-lg border border-[#D4A65A]/30 bg-[#FDF6E7] px-4 py-3 text-[13px] text-[#8C6B33]">
          Demo mode — approvals and rejections are disabled. Create a workspace
          to run the review flow on your own SOPs.
        </div>
      )}

      {/* Filter chips */}
      <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="learning-filter-bar">
        {[
          { key: "all", label: "All" },
          { key: "draft", label: "Draft" },
          { key: "approved", label: "Approved" },
          { key: "rejected", label: "Rejected" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            data-testid={`learning-filter-${f.key}`}
            className={`text-[12px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${
              statusFilter === f.key
                ? "bg-[#0B1220] text-white border-[#0B1220]"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
          >
            {f.label} <span className="ml-1 text-[11px] opacity-70">({counts[f.key]})</span>
          </button>
        ))}
      </div>

      <ErrorBanner error={error} className="mb-5" />

      {loading && <LoadingRow label="Loading learning modules…" />}

      {!loading && modules.length === 0 && (
        <EmptyState
          testid="learning-empty-state"
          icon={Sparkles}
          title="No learning drafts yet"
          description={
            <>
              Head to <Link to="/app/documents" className="text-[#8C6B33] font-semibold underline">Documents</Link>,
              open an SOP linked to a competency and choose <b>Generate learning</b>.
              CanniComply will draft a plain-English module and a mixed
              MCQ + scenario question bank for you to review.
            </>
          }
        />
      )}

      {!loading && modules.length > 0 && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500 text-sm">
          No modules match the <b>{statusFilter}</b> filter.
        </div>
      )}

      <div className="space-y-8" data-testid="learning-list">
        {grouped.map((g) => (
          <section key={g.id}>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-7 w-7 rounded-md bg-[#0B1220] text-[#D4A65A] flex items-center justify-center">
                <ShieldCheck size={13} />
              </span>
              <div className="font-display text-[15px] tracking-tight text-slate-900 font-semibold">
                {g.competency_name}
              </div>
              <span className="text-[11px] text-slate-500">· {g.modules.length}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {g.modules.map((m) => (
                <ModuleCard key={m.id} m={m} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </AppShell>
  );
}

function ModuleCard({ m }) {
  return (
    <Link
      to={`/app/learning-review/${m.id}`}
      data-testid={`learning-module-${m.id}`}
      className="rounded-xl border border-slate-200 bg-white p-5 hover:border-[#D4A65A]/60 hover:shadow-[0_2px_20px_-8px_rgba(11,18,32,0.15)] transition-all block"
    >
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-lg bg-[#D4A65A]/15 text-[#8C6B33] flex items-center justify-center shrink-0">
          <BookOpen size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={m.status} />
            <span className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500">
              {new Date(m.generated_at).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
          </div>
          <div className="mt-1.5 font-display text-[15.5px] tracking-tight text-slate-900 font-semibold line-clamp-2">
            {m.title}
          </div>
          <p className="mt-1.5 text-[12.5px] text-slate-500 line-clamp-2 leading-relaxed">
            {m.summary}
          </p>
          <div className="mt-3 flex items-center gap-x-3 gap-y-1 flex-wrap text-[11.5px] text-slate-500">
            <span className="inline-flex items-center gap-1">
              <FileText size={11} /> {m.document_title || "SOP"} v{m.document_version || "?"}
            </span>
            <span className="text-slate-300">·</span>
            <span>{(m.sections || []).length} sections</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

/* ---------------- DETAIL VIEW ---------------- */

function LearningDetail({ moduleId }) {
  const { business } = useAuth();
  const isDemo = business?.is_demo;
  const navigate = useNavigate();

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rejectingModule, setRejectingModule] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(null); // "module" | question id

  async function load() {
    setLoading(true);
    try {
      const data = await api.get(`/learning-modules/${moduleId}`);
      setPayload(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, [moduleId]);

  async function approveModule() {
    if (isDemo) return;
    setBusy("module");
    try {
      await api.post(`/learning-modules/${moduleId}/approve`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }
  async function rejectModule() {
    if (isDemo) return;
    setBusy("module");
    try {
      await api.post(`/learning-modules/${moduleId}/reject`, { reason: rejectReason });
      setRejectingModule(false);
      setRejectReason("");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }

  async function approveQuestion(qid) {
    if (isDemo) return;
    setBusy(qid);
    try {
      await api.post(`/questions/${qid}/approve`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }
  async function rejectQuestion(qid, reason) {
    if (isDemo) return;
    setBusy(qid);
    try {
      await api.post(`/questions/${qid}/reject`, { reason: reason || "" });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }
  async function saveQuestion(qid, patch) {
    if (isDemo) return false;
    setBusy(qid);
    try {
      await api.patch(`/questions/${qid}`, patch);
      await load();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Loading module…
        </div>
      </AppShell>
    );
  }
  if (!payload) {
    return (
      <AppShell>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-700 text-sm">
          {error || "Module not found."}
          <div className="mt-3">
            <button
              onClick={() => navigate("/app/learning-review")}
              className="text-[12px] font-semibold text-slate-700 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-1.5"
            >
              Back to list
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  const { module: m, questions } = payload;
  const approvedQuestions = (questions || []).filter((q) => q.status === "approved").length;
  const rejectedQuestions = (questions || []).filter((q) => q.status === "rejected").length;
  const pendingQuestions = (questions || []).filter((q) => q.status === "draft").length;

  return (
    <AppShell>
      {/* Header */}
      <div className="mb-6" data-testid="learning-detail">
        <button
          onClick={() => navigate("/app/learning-review")}
          data-testid="learning-back-btn"
          className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"
        >
          <ArrowLeft size={13} /> All learning modules
        </button>
        <div className="mt-4 flex items-start justify-between gap-5 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusPill status={m.status} />
              <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                {displayCompetency(m)}
              </span>
              <span className="text-slate-300">·</span>
              <span className="text-[11px] text-slate-500">
                {m.document_title} v{m.document_version}
              </span>
            </div>
            <h1 className="mt-2 font-display text-3xl tracking-tight text-slate-900 font-semibold">
              {m.title}
            </h1>
            {m.summary && (
              <p className="mt-2 text-slate-500 text-[14.5px] max-w-2xl leading-relaxed">
                {m.summary}
              </p>
            )}
          </div>
          {m.status === "draft" && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setRejectingModule(true)}
                disabled={isDemo || busy === "module"}
                data-testid="learning-module-reject-btn"
                className="text-[12px] font-semibold border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-40 rounded-lg px-3.5 py-2 inline-flex items-center gap-1.5"
              >
                <Ban size={13} /> Reject module
              </button>
              <button
                onClick={approveModule}
                disabled={isDemo || busy === "module"}
                data-testid="learning-module-approve-btn"
                className="text-[12px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 rounded-lg px-3.5 py-2 inline-flex items-center gap-1.5"
              >
                <BadgeCheck size={13} /> Approve module
              </button>
            </div>
          )}
        </div>
      </div>

      {m.rejected_reason && (
        <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
          <b>Rejected:</b> {m.rejected_reason}
        </div>
      )}

      {error && (
        <div className="mb-5 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Stats strip */}
      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Sections" value={(m.sections || []).length} />
        <StatTile label="Questions" value={(questions || []).length} />
        <StatTile label="Approved" value={approvedQuestions} tone="emerald" />
        <StatTile label="Pending" value={pendingQuestions} tone="amber" />
      </div>

      {/* Sections */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen size={16} className="text-[#8C6B33]" />
          <h2 className="font-display text-lg tracking-tight text-slate-900 font-semibold">
            Learning module
          </h2>
        </div>
        <div className="space-y-5" data-testid="learning-sections">
          {(m.sections || []).map((s, i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8C6B33] font-semibold">
                Section {i + 1}
              </div>
              <div className="mt-1 font-display text-[15.5px] tracking-tight text-slate-900 font-semibold">
                {s.heading}
              </div>
              <p className="mt-2 text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-line">
                {s.body}
              </p>
              {s.key_takeaways?.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {s.key_takeaways.map((k, ki) => (
                    <li key={ki} className="text-[12.5px] text-slate-600 flex items-start gap-2">
                      <CheckCircle2 size={12} className="text-emerald-600 mt-0.5 shrink-0" />
                      <span>{k}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.source_section && (
                <div className="mt-3 text-[11.5px] text-slate-500 border-t border-slate-200 pt-2 flex items-start gap-1.5">
                  <Quote size={11} className="mt-0.5 shrink-0" />
                  <span>Source: {s.source_section}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Questions */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#8C6B33]" />
            <h2 className="font-display text-lg tracking-tight text-slate-900 font-semibold">
              Assessment questions
            </h2>
          </div>
          <div className="text-[11.5px] text-slate-500">
            {approvedQuestions} approved · {pendingQuestions} pending · {rejectedQuestions} rejected
          </div>
        </div>
        <div className="space-y-4" data-testid="learning-questions">
          {(questions || []).map((q, i) => (
            <QuestionCard
              key={q.id}
              q={q}
              index={i}
              isDemo={isDemo}
              busy={busy === q.id}
              onApprove={() => approveQuestion(q.id)}
              onReject={(reason) => rejectQuestion(q.id, reason)}
              onSave={(patch) => saveQuestion(q.id, patch)}
            />
          ))}
        </div>
      </div>

      {/* Reject module modal */}
      {rejectingModule && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start md:items-center justify-center p-4 overflow-y-auto">
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-slate-200 p-6 my-auto"
            data-testid="learning-module-reject-modal"
          >
            <div className="font-display text-lg tracking-tight text-slate-900 font-semibold">
              Reject this learning module?
            </div>
            <p className="mt-1.5 text-[13px] text-slate-500 leading-relaxed">
              The draft will be marked rejected and won't be available for staff
              assessment. You can re-generate a fresh draft from the source
              document at any time.
            </p>
            <label className="mt-4 block text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
              Reason (optional)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              data-testid="learning-module-reject-reason"
              className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] focus:outline-none focus:border-slate-400"
              placeholder="e.g. Section 3 misrepresents the CD schedule cutoff."
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setRejectingModule(false);
                  setRejectReason("");
                }}
                className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 rounded-lg px-3 py-2"
              >
                Cancel
              </button>
              <button
                onClick={rejectModule}
                disabled={busy === "module"}
                data-testid="learning-module-reject-confirm"
                className="text-[12px] font-semibold bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40 rounded-lg px-3.5 py-2"
              >
                {busy === "module" ? "Rejecting…" : "Reject module"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function StatTile({ label, value, tone }) {
  const toneCls =
    tone === "emerald"
      ? "text-emerald-700"
      : tone === "amber"
      ? "text-amber-700"
      : "text-slate-900";
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
        {label}
      </div>
      <div className={`mt-1 font-display text-2xl font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

function QuestionCard({ q, index, isDemo, busy, onApprove, onReject, onSave }) {
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");
  const [editing, setEditing] = useState(false);
  const correct = q.correct_key;

  function startEdit() {
    setShowReject(false);
    setEditing(true);
  }

  return (
    <div
      data-testid={`learning-question-${q.id}`}
      className={`rounded-xl border p-4 md:p-5 ${
        q.status === "approved"
          ? "border-emerald-200 bg-emerald-50/40"
          : q.status === "rejected"
          ? "border-rose-200 bg-rose-50/40"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              Question {index + 1}
            </span>
            <span
              className={`text-[10px] uppercase tracking-[0.14em] font-semibold border rounded px-1.5 py-0.5 ${
                q.type === "scenario"
                  ? "border-[#D4A65A]/40 bg-[#FDF6E7] text-[#8C6B33]"
                  : "border-slate-200 bg-slate-100 text-slate-600"
              }`}
            >
              {q.type === "scenario" ? "Scenario" : "MCQ"}
            </span>
            <StatusPill status={q.status} />
          </div>
          {!editing && (
            <div className="mt-2 text-[14px] text-slate-900 font-medium leading-relaxed">
              {q.prompt}
            </div>
          )}
        </div>
        {q.status === "draft" && !editing && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowReject((s) => !s)}
              disabled={isDemo || busy}
              data-testid={`question-reject-btn-${q.id}`}
              className="text-[11.5px] font-semibold border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-40 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1"
            >
              <Ban size={12} /> Reject
            </button>
            <button
              onClick={startEdit}
              disabled={isDemo || busy}
              data-testid={`question-edit-btn-${q.id}`}
              className="text-[11.5px] font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1"
            >
              <Pencil size={12} /> Edit
            </button>
            <button
              onClick={onApprove}
              disabled={isDemo || busy}
              data-testid={`question-approve-btn-${q.id}`}
              className="text-[11.5px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1"
            >
              <CheckCircle2 size={12} /> Approve
            </button>
          </div>
        )}
      </div>

      {/* Edit form */}
      {editing && (
        <QuestionEditForm
          q={q}
          busy={busy}
          onCancel={() => setEditing(false)}
          onSave={async (patch) => {
            const ok = await onSave(patch);
            if (ok) setEditing(false);
          }}
        />
      )}

      {/* Options (read-only view) */}
      {!editing && (
        <div className="mt-4 space-y-1.5">
          {(q.options || []).map((opt) => {
            const isCorrect = opt.key === correct;
            return (
              <div
                key={opt.key}
                className={`rounded-lg border px-3 py-2 text-[13px] flex items-start gap-2 ${
                  isCorrect
                    ? "border-emerald-300 bg-emerald-50/70 text-emerald-900"
                    : "border-slate-200 bg-white text-slate-700"
                }`}
              >
                <span
                  className={`text-[11px] font-bold rounded w-5 h-5 inline-flex items-center justify-center shrink-0 ${
                    isCorrect ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {opt.key}
                </span>
                <span className="min-w-0">{opt.text}</span>
                {isCorrect && (
                  <span className="ml-auto text-[10.5px] uppercase tracking-[0.14em] font-semibold text-emerald-700 shrink-0">
                    Correct
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!editing && q.explanation && (
        <div className="mt-3 text-[12.5px] text-slate-600 border-t border-slate-200 pt-3 leading-relaxed">
          <span className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
            Rationale
          </span>
          <div className="mt-1">{q.explanation}</div>
        </div>
      )}

      {!editing && (q.source_section || q.source_quote) && (
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
          <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold flex items-center gap-1">
            <Quote size={10} /> Source
          </div>
          {q.source_section && (
            <div className="mt-1 text-[12px] text-slate-600">{q.source_section}</div>
          )}
          {q.source_quote && (
            <div className="mt-1 text-[12.5px] italic text-slate-700 border-l-2 border-[#D4A65A]/60 pl-2.5">
              “{q.source_quote}”
            </div>
          )}
        </div>
      )}

      {q.rejected_reason && (
        <div className="mt-3 text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
          <b>Rejected:</b> {q.rejected_reason}
        </div>
      )}

      {showReject && q.status === "draft" && (
        <div className="mt-3 rounded-lg bg-white border border-rose-200 p-3">
          <label className="block text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
            Reason (optional)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            data-testid={`question-reject-reason-${q.id}`}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] focus:outline-none focus:border-slate-400"
            placeholder="Why is this question being rejected?"
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              onClick={() => {
                setShowReject(false);
                setReason("");
              }}
              className="text-[11.5px] font-semibold text-slate-600 hover:text-slate-900 rounded-lg px-2.5 py-1.5"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onReject(reason);
                setShowReject(false);
                setReason("");
              }}
              disabled={busy}
              data-testid={`question-reject-confirm-${q.id}`}
              className="text-[11.5px] font-semibold bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40 rounded-lg px-3 py-1.5 inline-flex items-center gap-1"
            >
              <XCircle size={12} /> Confirm reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function QuestionEditForm({ q, busy, onCancel, onSave }) {
  const [prompt, setPrompt] = useState(q.prompt || "");
  const [options, setOptions] = useState(() => {
    const base = ["A", "B", "C", "D"];
    const map = Object.fromEntries((q.options || []).map((o) => [o.key, o.text]));
    return base.map((k) => ({ key: k, text: map[k] || "" }));
  });
  const [correctKey, setCorrectKey] = useState(q.correct_key || "A");
  const [explanation, setExplanation] = useState(q.explanation || "");
  const [sourceSection, setSourceSection] = useState(q.source_section || "");
  const [sourceQuote, setSourceQuote] = useState(q.source_quote || "");
  const [localError, setLocalError] = useState("");

  function setOptionText(key, text) {
    setOptions((prev) => prev.map((o) => (o.key === key ? { ...o, text } : o)));
  }

  async function submit() {
    setLocalError("");
    if (!prompt.trim()) {
      setLocalError("Question text cannot be empty.");
      return;
    }
    const cleaned = options
      .map((o) => ({ key: o.key, text: (o.text || "").trim() }))
      .filter((o) => o.text.length > 0);
    if (cleaned.length < 2) {
      setLocalError("Provide at least two answer options.");
      return;
    }
    if (!cleaned.find((o) => o.key === correctKey)) {
      setLocalError("The correct answer must match one of the filled-in options.");
      return;
    }
    await onSave({
      prompt: prompt.trim(),
      options: cleaned,
      correct_key: correctKey,
      explanation: explanation.trim(),
      source_section: sourceSection.trim(),
      source_quote: sourceQuote.trim(),
    });
  }

  return (
    <div
      className="mt-4 rounded-lg border border-slate-200 bg-white p-4 space-y-4"
      data-testid={`question-edit-form-${q.id}`}
    >
      <EditField label="Question">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={2}
          data-testid={`question-edit-prompt-${q.id}`}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-900 focus:outline-none focus:border-slate-400"
        />
      </EditField>

      <div>
        <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">
          Answer options — select the correct one
        </div>
        <div className="space-y-2">
          {options.map((opt) => (
            <label
              key={opt.key}
              className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                correctKey === opt.key
                  ? "border-emerald-300 bg-emerald-50/60"
                  : "border-slate-200 bg-white"
              }`}
            >
              <input
                type="radio"
                name={`correct-${q.id}`}
                checked={correctKey === opt.key}
                onChange={() => setCorrectKey(opt.key)}
                data-testid={`question-edit-correct-${q.id}-${opt.key}`}
                className="accent-emerald-600 shrink-0"
              />
              <span
                className={`text-[11px] font-bold rounded w-5 h-5 inline-flex items-center justify-center shrink-0 ${
                  correctKey === opt.key ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                {opt.key}
              </span>
              <input
                type="text"
                value={opt.text}
                onChange={(e) => setOptionText(opt.key, e.target.value)}
                data-testid={`question-edit-option-${q.id}-${opt.key}`}
                className="flex-1 min-w-0 bg-transparent text-[13px] text-slate-800 focus:outline-none"
                placeholder={`Option ${opt.key}`}
              />
            </label>
          ))}
        </div>
      </div>

      <EditField label="Rationale">
        <textarea
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          rows={2}
          data-testid={`question-edit-explanation-${q.id}`}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] text-slate-800 focus:outline-none focus:border-slate-400"
        />
      </EditField>

      <div className="grid md:grid-cols-2 gap-3">
        <EditField label="Source section">
          <input
            type="text"
            value={sourceSection}
            onChange={(e) => setSourceSection(e.target.value)}
            data-testid={`question-edit-source-section-${q.id}`}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] text-slate-800 focus:outline-none focus:border-slate-400"
            placeholder="e.g. Section 3.2"
          />
        </EditField>
        <EditField label="Source quote">
          <textarea
            value={sourceQuote}
            onChange={(e) => setSourceQuote(e.target.value)}
            rows={2}
            data-testid={`question-edit-source-quote-${q.id}`}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] text-slate-800 focus:outline-none focus:border-slate-400"
            placeholder="Verbatim line from the SOP"
          />
        </EditField>
      </div>

      {localError && (
        <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
          {localError}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid={`question-edit-cancel-${q.id}`}
          className="text-[11.5px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-40 rounded-lg px-3 py-1.5"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid={`question-edit-save-${q.id}`}
          className="text-[11.5px] font-semibold bg-[#0B1220] hover:bg-[#131c31] text-white disabled:opacity-40 rounded-lg px-3 py-1.5 inline-flex items-center gap-1"
        >
          <Save size={12} /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function EditField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

/* ---------------- ROUTE ENTRY ---------------- */

export default function LearningReview() {
  const { moduleId } = useParams();
  if (moduleId) return <LearningDetail moduleId={moduleId} />;
  return <LearningList />;
}
